import { Inject } from '@odinjs/odin';
import { CardAppointmentLocationAPI, CardAppointmentsTitleAPI } from '@philips-emr/patient-components/src';
import { ActionAPI, BaseModalProps, BreadcrumbAPI, ButtonAPI, ContextualBarAPI, DeckAPI, FetchAPI, FormAPI, MapAPI, Metadata, ModalService, PanelAPI, RepeaterAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { BrokerService, HttpService, MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core/lib/types';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { isMobile } from '@philips-internal/oswald-ui-utils';
import moment from 'moment';
import React, { ReactElement } from 'react';
import { ApplicationModulesIds } from '../../app/module-registry/application-modules';
import { AppointmentDetailSummaryAliases, FragmentNames, QuizQuestionAnswerViewAliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { APPOINTMENT_TYPE } from '../../constants/appointment-type';
import { CONTEXT_ATTRIBUTE } from '../../constants/context-attribute';
import { SCHEDULE_OTHER_STATUS, SCHEDULE_STATUS, WaitingListStatus } from '../../constants/schedule-status';
import { ScheduleType } from '../../constants/schedule-type';
import TASK_ITEM_STATUS from '../../constants/task-item-status';
import TASK_STATUS from '../../constants/task-status';
import { Appointment } from '../../interfaces/appointment';
import TaskItem from '../../interfaces/task-item';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';
import { cast } from '../../utils/cast-helpers';
import { defaultIfUndefined } from '../../utils/object-utils';
import RescheduleContext, { rescheduleCancelModal } from '../../utils/reschedule-utils';
import { getAppointmentType } from '../../utils/route-appointment-type-utils';
import { TaskListCardUtils } from '../../utils/task-list-card-utils';
import TasksContext from '../../utils/tasks-context';
import { showToastNotificationError } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import { UrlTelemedicine } from '../home/patient-root-controller';
import PseudonymCancelService from '../pseudonym-appointments/pseudonym-service/pseudonym-cancel-service';
import './appointment-detail-summary-controller.css';
import PseudonymUtils from '../pseudonym-appointments/pseudonym-utils';
import { ScheduleParameters } from '../../interfaces/schedule-parameters';
import { NO } from '../pseudonym-appointments/pseudonym-types';
import { RescheduleCheckup } from '../../interfaces/reschedule-checkup';

interface AppointmentDetailSummary {
  appointmentType?: APPOINTMENT_TYPE | null;
  nrSeq?: string;
  patientId?: string | number;
}

interface AppointmentDataValue{
  data: [],
  isConfirmationResult: boolean
}

interface DeckTaskListData {
  date: string;
  deadlineForAnswerTaskFormatted: string;
  deckGroupType: string;
  status: string;
  taskEventType: string;
  taskType: string;
}

enum DefaultValue {
  READ_ONLY = 'S',
}

enum DeckGroupTaskType {
  POST_PROCEDURE = 'POST',
  PRE_PROCEDURE = 'PREP',
}

enum ContextKeys {
  APPOINTMENT_TASK_EVENT_TYPE = 'appointmentTaskEventType',
}

@Controller({ fragment: FragmentNames.APPOINTMENT_DETAIL_SUMMARY })
export default class AppointmentDetailSummaryController extends BaseController {

  isTasksBlocked = false;

  @Inject(AppointmentDetailSummaryAliases.REPEATER_0)
    repeaterApi!: RepeaterAPI;

  @Inject(AppointmentDetailSummaryAliases.MAP_APPOINTMENT_LOCATION_1)
    mapAppointmentLocationApi!: MapAPI;

  @Inject(AppointmentDetailSummaryAliases.PANEL_MAP)
    panelMapApi!: PanelAPI;

  @Inject(AppointmentDetailSummaryAliases.CONTEXTUAL_BAR_0)
    contextualBarApi!: ContextualBarAPI;

  @Inject(AppointmentDetailSummaryAliases.BUTTON_EMAIL)
    buttonEmailApi!: ActionAPI;

  @Inject(AppointmentDetailSummaryAliases.BUTTON_RESCHEDULE)
    buttonRescheduleApi!: ActionAPI;

  @Inject(AppointmentDetailSummaryAliases.BUTTON_RESCHEDULE_MORE)
    buttonRescheduleMoreApi!: ActionAPI;

  @Inject(AppointmentDetailSummaryAliases.BUTTON_CANCEL)
    buttonCancelApi!: ActionAPI;

  @Inject(AppointmentDetailSummaryAliases.BUTTON_CONFIRM)
    buttonConfirmApi!: ActionAPI;

  @Inject(AppointmentDetailSummaryAliases.BUTTON_INSTRUCTIONS)
    buttonInstructionsApi!: ButtonAPI;

  @Inject(AppointmentDetailSummaryAliases.BUTTON_PRINT)
    buttonPrintApi!: ButtonAPI;

  @Inject(AppointmentDetailSummaryAliases.BUTTON_QUIZ)
    buttonQuizApi!: ButtonAPI;

  @Inject(AppointmentDetailSummaryAliases.PANEL_TASK_LIST)
    taskListPanelApi!: PanelAPI;

  @Inject(AppointmentDetailSummaryAliases.TASKS_ALERT_PANEL)
    tasksAlertPanelApi!: PanelAPI;

  @Inject(AppointmentDetailSummaryAliases.DECK_TASK_LIST)
    taskListDeckApi!: DeckAPI;

  @Inject(AppointmentDetailSummaryAliases.FETCH_TASK_LIST)
    fetchTaskListApi!: FetchAPI;

  @Inject(AppointmentDetailSummaryAliases.FETCH_APPOINTMENTS_DETAIL)
    fetchAppointmentsDetailApi!: FetchAPI;

  @Inject(AppointmentDetailSummaryAliases.BREADCRUMB_0)
    breadcumpApi!: BreadcrumbAPI;

  @Inject(QuizQuestionAnswerViewAliases.FORM_FETCH_QUIZ_QUESTION_ANSWER_VIEW)
    questionnaireFormAPI!: FormAPI;

  @Inject('PseudonymCancelService')
    pseudonymCancelService!: PseudonymCancelService;

  private getExpressionForDeckLeftHeader(date: moment.Moment, actualDate: moment.Moment, status: string, areAllTasksFinished: boolean): string {
    const difInDays = date.diff(actualDate.clone().startOf('day'), 'days');
    const difInMinutes = date.diff(actualDate, 'minutes');
    const translateMsg = (expression: string): string => MessageService.translate(expression);

    if (areAllTasksFinished) return translateMsg('patient.finalized');
    if (difInMinutes < 0 || status === TASK_ITEM_STATUS.BLOCKED) {
      this.isTasksBlocked = true;
      return MessageService.translate('patient.blocked');
    }
    if (difInDays === 0) return translateMsg('tws.datepicker.today');
    if (difInDays === 1) return `${difInDays} ${translateMsg('patient.day-left')}`;

    return `${difInDays} ${MessageService.translate('patient.days-left')}`;
  }

  @OnDynamicContentRender({ target: 'deck-task-list' })
  handleDeckGroupRender(event: RenderEvent): void {
    const { render } = event;
    const deckData = event.data as DeckTaskListData[];
    const areAllTasksFinished = deckData.every(task => task.status === TASK_ITEM_STATUS.COMPLETED);
    const [firstDeckItemGroup] = deckData;
    const date = moment(firstDeckItemGroup.deadlineForAnswerTaskFormatted);
    const actualDate = moment();
    const scheduleHour = date.format('HH:mm');
    const dateFormat = ModelService.format('date', date, { type: 'datePicker' });
    const timeLeftExpression = this.getExpressionForDeckLeftHeader(date, actualDate, firstDeckItemGroup.status, areAllTasksFinished);

    const renderSuggestionDate: () => ReactElement = () => {
      return (
        <div className='deck-appointments-detail-controller-task-list'  >
          <div className='task-list-do-until-date'>
            <span>{MessageService.translate('patient.do-until')}</span>
            <span className='task-list-date'>{` ${dateFormat} - ${scheduleHour}`}</span>
          </div>
          <span className='task-list-days-left'>{timeLeftExpression}</span>
        </div>
      );
    }
    render(renderSuggestionDate);
  }

  @OnAfterFetch({ target: AppointmentDetailSummaryAliases.FETCH_TASK_LIST })
  handleAfterFetchTaskList(event: FetchEvent): void {
    let panelTasksAlertVisibility = true;
    const taskList = event.data as TaskItem[];
    const hasAnyTaskToDo = taskList?.some(task => task.ieStatusTarefa === TASK_STATUS.PENDING);
    const ieScheduleStatus = this.context.get('ieScheduleStatus') as string;
    const isScheduleCanceled = ieScheduleStatus === SCHEDULE_STATUS.CANCELED;
    const actualDate = moment();
    const hasTaskTimeExpired = taskList.every(task => moment(task.deadlineForAnswerTaskFormatted).diff(actualDate, 'minutes') < 0);
    const doesNotHasMoreTasksToAnswer = taskList.every(task =>
      task.statusTaskCalculated === TASK_ITEM_STATUS.BLOCKED || task.statusTaskCalculated === TASK_ITEM_STATUS.COMPLETED
    );

    if (taskList.length === 0) {
      this.taskListPanelApi.setVisible(false);
      this.tasksAlertPanelApi.setVisible(false);
      return;
    }

    if (!hasAnyTaskToDo || hasTaskTimeExpired || isScheduleCanceled || doesNotHasMoreTasksToAnswer) {
      panelTasksAlertVisibility = false;
    }

    this.taskListPanelApi.setVisible(panelTasksAlertVisibility);
    this.tasksAlertPanelApi.setVisible(panelTasksAlertVisibility);
    const deckData = taskList.map<DeckTaskListData>(taskInfo => {
      const deckRow: DeckTaskListData = {
        date: taskInfo.dsDate,
        deadlineForAnswerTaskFormatted: taskInfo.deadlineForAnswerTaskFormatted,
        deckGroupType: '',
        status: taskInfo.statusTaskCalculated,
        taskEventType: taskInfo.typeCadEvent,
        taskType: taskInfo.ieTipoTarefa,
      }

      if (TaskListCardUtils.isPostProcedureType(taskInfo.typeCadEvent)) {
        deckRow.deckGroupType = DeckGroupTaskType.POST_PROCEDURE;
      } else if (TaskListCardUtils.isPreparationType(taskInfo.typeCadEvent)) {
        deckRow.deckGroupType = DeckGroupTaskType.PRE_PROCEDURE;
      }

      return deckRow;
    });

    this.taskListDeckApi.setData(cast([...deckData]));
  }

  @OnMount({ target: AppointmentDetailSummaryAliases.FETCH_APPOINTMENTS_DETAIL })
  handleMount(): void {
    this.prepareFetch();
  }

  private prepareFetch(nrSeqAgenda?: string): void {
    const {
      nrSeqAgendaConsulta,
      nrSeqAgendaExame,
      nrSeqAgendaServico,
      nrSeqAgendaCirurgica,
      nrSeqAgendaQuimio,
      nrSeqAgendaRequest,
      nrSequenceWaitingList
    } = this.historyService.getCurrentRouteParams();

    let patientId = UserContext.patientId || '';

    let nrSeq = nrSeqAgenda
            || nrSeqAgendaConsulta as Value
            || nrSeqAgendaExame as Value
            || nrSeqAgendaServico as Value
            || nrSeqAgendaCirurgica as Value
            || nrSeqAgendaQuimio as Value
            || nrSeqAgendaRequest  as Value
            || nrSequenceWaitingList as Value;

    const currentRoute = this.historyService.getCurrentRoute();
    let appointmentType = getAppointmentType(currentRoute);

    let ieScheduleType = '';
    if (appointmentType === APPOINTMENT_TYPE.REQUEST) {
      ieScheduleType = ScheduleType.REQUEST;
      appointmentType = APPOINTMENT_TYPE.CONSULTATIONS;
    }

    if (nrSeq == null || appointmentType == null) {
      const appointmentDetailSummary = this.context.get(CONTEXT_ATTRIBUTE.APPOINTMENT_DETAIL) as AppointmentDetailSummary;
      if (appointmentDetailSummary) {
        nrSeq = String(appointmentDetailSummary.nrSeq) || '';
        appointmentType = appointmentDetailSummary.appointmentType || null;
        patientId = appointmentDetailSummary.patientId || '';
      }

    }

    if (appointmentType != null) {
      const endpoint = APPLICATION_ENDPOINTS.APPOINTMENT_DETAIL;
      const params = {
        appointmentType,
        ieScheduleType,
        nrSeq,
        patientId,
      } as Record<string, Value>;

      this.context.set({
        ieScheduleType: appointmentType,
        nrSeqAgenda: nrSeq,
        patientId,
      });

      if (endpoint && params) {
        this.context.set({ appointmentType, ieScheduleType, nrSeq, patientId });
        this.fetchAppointmentsDetailApi.setEndpoint(APPLICATION_ENDPOINTS.APPOINTMENT_DETAIL);
        this.fetchAppointmentsDetailApi.setParameters(params);
        this.fetchAppointmentsDetailApi.forceFetch();
      }
    }
  }

  parseScheduleType(ieTipoAgendamento: string): APPOINTMENT_TYPE {
    if (ieTipoAgendamento) {
      switch (ieTipoAgendamento) {
        case ScheduleType.CONSULTATION: return APPOINTMENT_TYPE.CONSULTATIONS;
        case ScheduleType.EXAM: return APPOINTMENT_TYPE.EXAMS;
        case ScheduleType.SERVICE: return APPOINTMENT_TYPE.SERVICES;
        case ScheduleType.SURGERY: return APPOINTMENT_TYPE.SURGERIES;
        case ScheduleType.CHEMOTHERAPY: return APPOINTMENT_TYPE.CHEMOTHERAPIES;
      }
    }
    return APPOINTMENT_TYPE.UNKNOW;
  }

  @OnMount(AppointmentDetailSummaryAliases.CARD_APPOINTMENTS_DETAIL_LOCATION_1)
  handleLocationMount(evt:MountEvent): void {
    const cardAppointmentLocation = evt.target as CardAppointmentLocationAPI;
    const ieTelemedicina = this.context.get('ieTelemedicina');
    if (ieTelemedicina === 'S') {
      cardAppointmentLocation.setOnEnterTeleconsultation(this.handleEnterTeleconsultation);
    }
  }

  @OnMount(AppointmentDetailSummaryAliases.AVATAR_DOCTOR)
  handleTitleMount(evt:MountEvent): void {
    const cardAppointmentTitle = evt.target as CardAppointmentsTitleAPI;
    const isConfirmationResult = this.context.get<boolean>('isConfirmationResult')
    cardAppointmentTitle.setShowSubtitle(isConfirmationResult);
  }

  @OnAfterFetch(AppointmentDetailSummaryAliases.FETCH_APPOINTMENTS_DETAIL)
  async handleAfterFetch(evt: FetchEvent): Promise<void> {
    const data = evt.data as unknown as Appointment;
    const {
      dsLinkMapa,
      dsTypeSchedule,
      dsOrientacao,
      ieScheduleType,
      ieScheduleTypeOrigin,
      ieTelemedicina,
      ieStatus,
      ieStatusListaEspera,
      cdMedico,
      nrSeqAgendaInt,
      nrSeqAgendaIntItem,
      nrSeqProcInterno,
      scheduleCode,
      dtAgendamento,
      taskEventType,
    } = data;

    this.contextualBarApi.setTitle(dsTypeSchedule as string);

    if (ieScheduleType) {
      this.context.set({ ieScheduleType });
    }

    this.context.set({
      cdMedico,
      dsLinkMapa,
      dsOrientacao,
      ieScheduleStatus: ieStatus as string,
      ieStatusListaEspera,
      ieTelemedicina,
      nrSeqAgendaInt,
      nrSeqAgendaIntItem,
      nrSeqProcInterno,
      scheduleCode,
      [ContextKeys.APPOINTMENT_TASK_EVENT_TYPE]: taskEventType,
    });

    const quizEnabled = await this.isEnableQuizButton(nrSeqAgendaInt, nrSeqAgendaIntItem);
    const cancelEnabled = this.isEnableCancelButton(RescheduleContext.scheduleParameters, ieStatus, ieScheduleType, ieStatusListaEspera);
    const confirmEnabled = this.isConfirmButtonEnabled(RescheduleContext.scheduleParameters, ieStatus, ieScheduleType, ieStatusListaEspera);
    const reScheduleEnabled = this.isEnableReScheduleDropdown(ieStatus, ieScheduleType, ieScheduleTypeOrigin);
    const isInstructionsButtonEnable = this.isEnableIntructionsButton(ieStatus, dsOrientacao);
    const isScheduleActive = (ieStatus === SCHEDULE_STATUS.APPROVED) || (ieStatus === SCHEDULE_STATUS.CONFIRMED);

    this.buttonQuizApi.setEnabled(quizEnabled);
    this.buttonRescheduleApi.setEnabled(reScheduleEnabled);
    this.buttonRescheduleMoreApi.setEnabled(reScheduleEnabled);
    this.buttonCancelApi.setEnabled(cancelEnabled);
    this.buttonConfirmApi.setEnabled(confirmEnabled);
    this.buttonEmailApi.setEnabled(isScheduleActive);
    this.buttonInstructionsApi.setEnabled(isInstructionsButtonEnable);
    this.buttonPrintApi.setEnabled(true);

    if (ieTelemedicina === 'S' && cancelEnabled && ((ieStatus === SCHEDULE_STATUS.APPROVED)
        || (ieStatus === SCHEDULE_STATUS.CONFIRMED) || (ieStatus === SCHEDULE_OTHER_STATUS.EXECUTION))) {
      const actualDate = moment().startOf('day');
      const dateDiffInDays = moment(dtAgendamento).diff(actualDate, 'days');

      if (dateDiffInDays === 0) {
        const newInlineActionsDesktopLimit = this.contextualBarApi.getInlineActionsDesktopLimit() + 1;
        this.contextualBarApi.setInlineActionsDesktopLimit(newInlineActionsDesktopLimit);
      }
    }

    if (this.historyService.getCurrentRoute().includes('/print')) {
      setTimeout(() => {
        window.print();
        this.historyService.back();
      }, 2000);
    }

    this.breadcumpApi.setValues({appointments: 'patient.home.tab.appointments'});

    this.repeaterApi.setData([evt.data as Record<string, Value>]);
  }

  @OnMount(AppointmentDetailSummaryAliases.PANEL_TASK_LIST)
  handlePanelTaskListMount(event: MountEvent): void {
    const isConfirmationResult =  this.context.get<boolean>('isConfirmationResult');
    if (isConfirmationResult) {
      const taskListPanelApi = event.target as PanelAPI;
      taskListPanelApi.setVisible(false);
    }
  }

  @OnMount(AppointmentDetailSummaryAliases.FETCH_TASK_LIST)
  handleFetchTaskList(event: MountEvent): void {
    const fetchApi = event.target as FetchAPI;
    const nrSeqAgenda = this.context.get<string>('nrSeqAgenda');
    const patientId = this.context.get<string>('patientId');

    const params = {
      eventType: this.context.get<string>(ContextKeys.APPOINTMENT_TASK_EVENT_TYPE),
      nrSeqAgenda,
      patientId
    };
    fetchApi.setEndpoint(APPLICATION_ENDPOINTS.GET_TASK_LIST);
    fetchApi.setParameters(params);
    fetchApi.forceFetch();
  }

  private handleEnterTeleconsultation = (nrSeqAgendaConsulta: number, ieScheduleType: string): void => {
    const patientId = UserContext.patientId;

    PatientHttpService.get(APPLICATION_ENDPOINTS.GET_URL_TELEMEDICINE, {
      ieScheduleType,
      nrSeqAgendaConsulta,
      patientId
    }).then(response => {
      const urlTelemedicina = response.body as UrlTelemedicine;
      BrokerService.publishToTopic('iframe-teleconsulta', { patientUrl: urlTelemedicina.patientUrl });
    }).catch(({ response }: HttpServiceError<string>) => {
      const message = response?.body || 'newaccount.error.internal.server.error.try.again';
      showToastNotificationError(message);
    });
  }

  private isEnableIntructionsButton( ieStatus?: string, dsOrientacao?: string ): boolean {
    return (
      ieStatus !== SCHEDULE_STATUS.CANCELED
      && ieStatus !== SCHEDULE_STATUS.MISSED
      && ieStatus !== SCHEDULE_OTHER_STATUS.CANCELED
      && dsOrientacao !== undefined
    );
  }

  private isEnableQuizButton(nrSeqAgendaInt?: number, nrSeqAgendaIntItem?: number): Promise<boolean> {
    if (!nrSeqAgendaInt || !nrSeqAgendaIntItem) {
      return Promise.resolve(false);
    }
    const quizMetadataParameters = {
      nrSeqAgendaIntItem: nrSeqAgendaIntItem,
      readOnly: DefaultValue.READ_ONLY,
      seqAgeInt: nrSeqAgendaInt
    };
    return HttpService.get<Metadata>(APPLICATION_ENDPOINTS.GET_QUIZ_METADATA, quizMetadataParameters).then(response => response.body?.children !== undefined);
  }

  private isEnableCancelButton(allowParameters: ScheduleParameters, ieStatus?: string, ieScheduleType?: string, ieStatusListaEspera?: string): boolean {
    if (ieStatusListaEspera) {
      return ieStatusListaEspera !== WaitingListStatus.CANCELLED;
    }

    const allowByConfiguration = {
      [ScheduleType.CHECKUP]: allowParameters.allowCancelCheckup,
    } as Record<string, Value>;

    if (ieScheduleType && allowByConfiguration[ieScheduleType] === NO) {
      return false;
    }

    const excludedStatuses = [
      SCHEDULE_STATUS.CANCELED,
      SCHEDULE_STATUS.MISSED,
      SCHEDULE_STATUS.DONE,
      SCHEDULE_OTHER_STATUS.CANCELED,
      SCHEDULE_OTHER_STATUS.EXECUTION
    ] as Array<string>;

    const validScheduleTypes = [
      ScheduleType.CHECKUP,
      ScheduleType.CONSULTATION,
      ScheduleType.REQUEST,
      ScheduleType.EXAM,
      ScheduleType.SERVICE
    ] as Array<string>;

    return !excludedStatuses.includes(defaultIfUndefined(ieStatus, ''))
    && validScheduleTypes.includes(defaultIfUndefined(ieScheduleType,''));
  }

  private isConfirmButtonEnabled(allowParameters: ScheduleParameters, status?: string, scheduleType?: string, waitingListStatus?: string): boolean {
    if (waitingListStatus) {
      return waitingListStatus !== WaitingListStatus.CANCELLED;
    }

    const allowByConfiguration = {
      [ScheduleType.CHECKUP]: allowParameters.allowConfirmCheckup,
    } as Record<string, Value>;

    if (scheduleType && allowByConfiguration[scheduleType] === NO) {
      return false;
    }

    const notAllowedStatus = [
      SCHEDULE_STATUS.CANCELED,
      SCHEDULE_STATUS.MISSED,
      SCHEDULE_STATUS.CONFIRMED,
      SCHEDULE_STATUS.DONE,
      SCHEDULE_OTHER_STATUS.CANCELED,
      SCHEDULE_OTHER_STATUS.EXECUTION
    ] as Array<string>;

    const allowedScheduleTypes = [
      ScheduleType.CHECKUP,
      ScheduleType.CONSULTATION,
      ScheduleType.EXAM,
      ScheduleType.SERVICE
    ] as Array<string>;

    return !notAllowedStatus.includes(defaultIfUndefined(status, ''))
    && allowedScheduleTypes.includes(defaultIfUndefined(scheduleType, ''));
  }

  private isEnableReScheduleDropdown(ieStatus?: string, ieScheduleType?: string, ieScheduleTypeOrigin?: string): boolean {
    const { reagendarConsulta, reagendarExame, reagendarServico, rescheduleCheckup } = RescheduleContext.rescheduleParameters;

    if (ieScheduleType === ScheduleType.REQUEST) {
      ieScheduleType = ieScheduleTypeOrigin;
    }

    const allowedConfiguration = {
      [ScheduleType.CHECKUP]: rescheduleCheckup,
      [ScheduleType.CONSULTATION]: reagendarConsulta,
      [ScheduleType.EXAM]: reagendarExame,
      [ScheduleType.SERVICE]: reagendarServico,
    } as Record<string, string>;

    if (ieScheduleType && allowedConfiguration[ieScheduleType] === NO) {
      return false;
    }

    const allowedScheduleTypes = [
      ScheduleType.CHECKUP,
      ScheduleType.CONSULTATION,
      ScheduleType.EXAM,
      ScheduleType.SERVICE,
    ] as Array<string>;

    const allowedStatus = [
      SCHEDULE_OTHER_STATUS.EXECUTION,
      SCHEDULE_STATUS.AWAITING_APPROVAL,
      SCHEDULE_STATUS.NOT_APPROVED,
      SCHEDULE_STATUS.CONFIRMED,
    ] as Array<string>;

    return allowedStatus.includes(ieStatus ?? '') && allowedScheduleTypes.includes(ieScheduleType ?? '');
  }

  @OnClick({ target: [AppointmentDetailSummaryAliases.BUTTON_RESCHEDULE] })
  handleClickButtonReSchedule(): void {
    if (this.getIeScheduleType() === ScheduleType.CHECKUP) {

      HttpService.get(APPLICATION_ENDPOINTS.GET_CHECKUP_DETAIL, {checkupSequence: this.getNrSeqAgenda()})
        .then(response => {
          RescheduleContext.rescheduleCheckup = response.body as RescheduleCheckup;
          this.historyService.push(APPLICATION_ROUTES.RESCHEDULE_CHECKUP);
        });
    } else {
      const listItem = this.getNrSeqAgenda() + ',';
      const body = (this.getIeScheduleType() === ScheduleType.CONSULTATION || this.getIeScheduleType() === ScheduleType.SERVICE)
        ? { listaConsultaP: listItem, listaExameP: '' }
        : { listaConsultaP: '', listaExameP: listItem };

      HttpService.post(
        APPLICATION_ENDPOINTS.RESCHEDULE_ITEMS_TRANSFERENCE, { patientId: UserContext.patientId }, { body }
      ).then(response => {
        RescheduleContext.nrSeqAgeint = response.body as number;
        this.historyService.push(APPLICATION_ROUTES.RESCHEDULE_APPOINTMENTS);
      });
    }
  }

  @OnClick({ target: [AppointmentDetailSummaryAliases.BUTTON_RESCHEDULE_MORE] })
  handleClickButtonReScheduleMore(): void {
    const { patientId } = UserContext;

    ModalService.openFullscreen({
      closeable: true,
      contextData: { patientId },
      fragment: FragmentNames.RESCHEDULE_TRANSFERENCE_MODAL,
      id: FragmentNames.RESCHEDULE_TRANSFERENCE_MODAL,
      onBeforeClose: (event) => {
        event.abort();

        if (RescheduleContext.hasInsuranceSelected) {
          ModalService.openDialog(rescheduleCancelModal);
        } else {
          ModalService.close(FragmentNames.RESCHEDULE_TRANSFERENCE_MODAL);
          RescheduleContext.hasInsuranceSelected = false;
        }
      },
      onClose: () => { return },
      title: 'reschedule.title',
    });
  }

  @OnClick({ target: [AppointmentDetailSummaryAliases.BUTTON_EMAIL] })
  handleConfirmSendMailClick(): void {
    const nrSeqAgenda = this.getNrSeqAgenda();
    const ieScheduleType = this.getIeScheduleType();
    const mailParams = {
      emailType: 'appointment',
      params: {
        ieScheduleType,
        nrSeqAgenda,
      }
    }
    const onModalClose = (): void => {
      return;
    };
    ModalService.openDefault({
      closeable: true,
      contextData: mailParams,
      dockButtons: [],
      fragment: FragmentNames.SEND_MAIL,
      onClose: onModalClose,
      title: 'patient.send-by-email',
    });
  }

  @OnClick({ target: [AppointmentDetailSummaryAliases.BUTTON_CANCEL] })
  handleOpenDropDownCancel(): void {
    const ieStatus = this.context.get('ieScheduleStatus') as string;
    const ieScheduleType = this.context.get('ieScheduleType') as string;
    const ieStatusListaEspera = this.context.get('ieStatusListaEspera') as string;
    const canOpenCancelDropDown = this.isEnableCancelButton(RescheduleContext.scheduleParameters, ieStatus, ieScheduleType, ieStatusListaEspera);

    if (!canOpenCancelDropDown) {
      return;
    }

    const { patientId } = UserContext;

    const {
      nrSeqAgendaConsulta,
      nrSeqAgendaExame,
      nrSeqAgendaCirurgica,
      nrSeqAgendaQuimio,
      nrSeqAgendaRequest,
      nrSequenceWaitingList,
      nrSeqAgendaServico
    } = this.historyService.getCurrentRouteParams();

    const nrSeqAgenda = nrSeqAgendaConsulta
      || nrSeqAgendaExame
      || nrSeqAgendaCirurgica
      || nrSeqAgendaQuimio
      || nrSeqAgendaRequest
      || nrSeqAgendaServico;

    const cancelSubmitAction = (): void => {
      this.prepareFetch();
      this.fetchTaskListApi.forceFetch();
    };
    const cancelSubmitWaitingListAction = (): void => { this.prepareFetch(nrSequenceWaitingList as string) };

    ieScheduleType === ScheduleType.WAITING_LIST
      ? this.pseudonymCancelService.cancelWaitingList(patientId, nrSeqAgenda as number, cancelSubmitWaitingListAction)
      : this.pseudonymCancelService.cancelItem(patientId, nrSeqAgenda as number, ieScheduleType, ieStatus, cancelSubmitAction);
  }

  @OnClick({ target: AppointmentDetailSummaryAliases.BUTTON_CONFIRM })
  handleOnClickButtonConfirm(): void {
    const patientId = UserContext.patientId;
    const scheduleCode = this.context.get('scheduleCode') as number;
    const scheduleSequenceNumber = this.getNrSeqAgenda() as number;
    const scheduleType = this.context.get('ieScheduleType') as string;

    const confirmSubmitAction = (): void => {
      this.prepareFetch();
      this.fetchTaskListApi.forceFetch();
    };

    this.pseudonymCancelService.confirmItem(patientId, scheduleCode, scheduleSequenceNumber, scheduleType, confirmSubmitAction);
  }

  getNrSeqAgenda(): number {
    const {
      nrSeqAgendaConsulta,
      nrSeqAgendaExame,
      nrSeqAgendaServico,
      nrSeqAgendaCirurgica,
      nrSeqAgendaQuimio,
      nrSeqAgendaRequest
    } = this.historyService.getCurrentRouteParams();

    return (
      nrSeqAgendaConsulta
      || nrSeqAgendaExame
      || nrSeqAgendaServico
      || nrSeqAgendaCirurgica
      || nrSeqAgendaQuimio
      || nrSeqAgendaRequest) as number;
  }

  getIeScheduleType(): ScheduleType | undefined {
    const {
      nrSeqAgendaConsulta,
      nrSeqAgendaExame,
      nrSeqAgendaServico,
      nrSeqAgendaCirurgica,
      nrSeqAgendaQuimio,
      nrSeqAgendaRequest
    } = this.historyService.getCurrentRouteParams();

    if (nrSeqAgendaConsulta) { return ScheduleType.CONSULTATION }
    if (nrSeqAgendaExame) { return ScheduleType.EXAM }
    if (nrSeqAgendaServico) { return ScheduleType.SERVICE }
    if (nrSeqAgendaCirurgica) { return ScheduleType.SURGERY }
    if (nrSeqAgendaQuimio) { return ScheduleType.CHEMOTHERAPY }
    if (nrSeqAgendaRequest) { return ScheduleType.REQUEST }
    return undefined;
  }

  @OnClick(AppointmentDetailSummaryAliases.TASKS_ALERT_LINK)
  handleClickAlertLink(): void {
    const nrSeqAgenda = this.context.get('nrSeqAgenda') as string;
    const taskList: DeckTaskListData[] = cast(this.taskListDeckApi.getData());
    TasksContext.nrSeqAgenda = parseInt(nrSeqAgenda);
    TasksContext.eventType = this.getTaskEventOnClickToast(taskList);
    TasksContext.taskDateToFilter = taskList.find(task => task.taskEventType === TasksContext.eventType)?.date;

    PseudonymUtils.setDefaultContextInformation();
    applicationContext.setHiddenModule(ApplicationModulesIds.TASKS, false);
    applicationContext.openModule(ApplicationModulesIds.TASKS, { preventNavigation: false });
  }

  private getTaskEventOnClickToast(taskList: DeckTaskListData[]): string {

    const tasksUncompleted = taskList.filter(task =>
      task.status !== TASK_ITEM_STATUS.BLOCKED && task.status !== TASK_ITEM_STATUS.COMPLETED
    );

    if (!tasksUncompleted) {
      return '';
    }

    let unCompletedTaskEventType = tasksUncompleted.find(task => task.deckGroupType === DeckGroupTaskType.PRE_PROCEDURE)?.taskEventType;
    if (!unCompletedTaskEventType) {
      unCompletedTaskEventType = tasksUncompleted.find(task => task.deckGroupType === DeckGroupTaskType.POST_PROCEDURE)?.taskEventType;
    }

    return defaultIfUndefined(unCompletedTaskEventType, '');
  }

  @OnClick({ target: [AppointmentDetailSummaryAliases.BUTTON_PRINT] })
  onPrintClick(): void {
    window.print();
  }

  @OnClick({ target: AppointmentDetailSummaryAliases.BUTTON_INSTRUCTIONS })
  onInstructionsClick(): void {

    const dsOrientacao = this.context.get('dsOrientacao');

    const onModalClose = (): void => {
      return;
    };
    if (!isMobile()) {
      ModalService.openDefault({
        closeable: true,
        contextData: { dsOrientacao },
        dockButtons: [],
        fragment: FragmentNames.INSTRUCTIONS,
        onClose: onModalClose,
        title: MessageService.translate('patient.instructions'),
      });
    } else {
      ModalService.openFullscreen({
        closeable: true,
        contextData: { dsOrientacao },
        dockButtons: [],
        fragment: FragmentNames.INSTRUCTIONS,
        onClose: onModalClose,
        title: MessageService.translate('patient.instructions')
      });
    }

  }

  @OnMount(AppointmentDetailSummaryAliases.MAP_APPOINTMENT_LOCATION_1)
  handleMountPanelMap(event:MountEvent): void {
    const mapApi = event.target as MapAPI;
    const dsLinkMapa = this.context.get('dsLinkMapa');
    const ieTelemedicina = this.context.get('ieTelemedicina');

    if (dsLinkMapa && ieTelemedicina !== 'S') {
      this.panelMapApi.setVisible(true);
      mapApi.setProviderUrl('https://www.google.com/maps/embed/v1/place?q=' + dsLinkMapa);
      mapApi.setLoading(false);
    } else {
      mapApi.setLoading(false);
      mapApi.setVisible(false);
    }
  }

  @OnMount(AppointmentDetailSummaryAliases.REPEATER_0)
  handleMountPanelRepeater(event: MountEvent): void {
    const repeaterApi = event.target as RepeaterAPI;
    this.getAppointmentConfirmationList(repeaterApi);
  }

  getAppointmentConfirmationList(repeaterApi: RepeaterAPI): void {
    const appointmentDataValue = this.context.get('appointment_confirmation_list') as AppointmentDataValue;
    if (appointmentDataValue) {
      this.context.set({isConfirmationResult: appointmentDataValue.isConfirmationResult});
      const currentData = repeaterApi.getData();
      if (currentData && currentData.length) {
        appointmentDataValue.data.forEach(data=>{
          currentData.push(data);
        });
        repeaterApi.setData(currentData);
      } else {
        repeaterApi.setData(appointmentDataValue.data);
      }
    }
  }

  @OnMount(AppointmentDetailSummaryAliases.CONTEXTUAL_BAR_0)
  handleMountContextualBar(): void {
    const isConfirmationResult = this.context.get<boolean>('isConfirmationResult')
    if (isConfirmationResult) {
      this.buttonQuizApi.setEnabled(false);
      this.buttonCancelApi.setEnabled(false);
      this.buttonConfirmApi.setEnabled(false);
      this.buttonEmailApi.setEnabled(false);
      this.buttonRescheduleApi.setEnabled(false);
      this.buttonRescheduleMoreApi.setEnabled(false);
      this.buttonInstructionsApi.setEnabled(false);
      this.buttonPrintApi.setEnabled(false);
      this.breadcumpApi.setValues([] as unknown as Record<string, string>);
    }
  }

  @OnClick({ target: AppointmentDetailSummaryAliases.BUTTON_QUIZ })
  openModalQuiz(): void {
    const nrSeqAgendaInt = this.context.get('nrSeqAgendaInt') as number;
    const nrSeqAgendaIntItem = this.context.get('nrSeqAgendaIntItem') as number;

    const params = {
      nrSeqAgendaIntItem: nrSeqAgendaIntItem,
      readOnly: DefaultValue.READ_ONLY,
      seqAgeInt: nrSeqAgendaInt,
    };

    const baseModalProps: BaseModalProps = {
      className: '',
      closeable: true,
      contextData: params,
      dockButtons: [],
      fragment: FragmentNames.QUIZ_QUESTION_ANSWER_VIEW,
      onClose: () => { return; },
      title: MessageService.translate('patient.questionnaire')
    }

    isMobile() ? ModalService.openFullscreen(baseModalProps) : ModalService.openDefault(baseModalProps)
  }
}
